import { yupResolver } from "@hookform/resolvers/yup"
import React, { useState, useEffect } from "react"
import { FormProvider, useForm } from "react-hook-form"
import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormFeedback,
  Spinner,
  Form,
  FormGroup,
  Label,
} from "reactstrap"
import * as yup from "yup"
import LoadingButton from "../../../components/Common/LoadingButton"
import { UFInput, UFInputNumber } from "../../../components/Common/Fields/Input"
import classnames from "classnames"
import closeIcon from "../../../assets/icons/close.svg"
import { Link } from "react-router-dom"
import { toastError, toastSuccess } from "../../../helpers/msg_helper"
import { postSettingsPaymentType } from "../../../services/Settings/settings"
import CurrencyList from "../../../components/Common/chooseFields/currency"
import DayMonth from "../../../components/Common/chooseFields/dayMonth"
import { getCode } from "../../../services/inventory"

const schema = yup.object().shape({
  name: yup.string().required("Payment Type is Required"),

  period: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .typeError("Period must be a number")
    .test("period-required-if-unit", "Period is Required", function (value) {
      const { unit } = this.parent
      if (unit && (value === null || value === undefined)) {
        return false
      }
      return true
    })
    .test("period-min-if-unit", "Period must be at least 1", function (value) {
      const { unit } = this.parent
      if (unit && value !== null && value < 1) {
        return false
      }
      return true
    }),

  unit: yup
    .mixed()
    .nullable()
    .test("unit-required-if-period", "Type is Required", function (value) {
      const { period } = this.parent
      if (period > 0 && !value) {
        return false
      }
      return true
    }),
})

const Edit = ({ loading, onUpdate, id, error, close, ...props }) => {
  const methods = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    shouldFocusError: true,
  })

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = methods

  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const getUniqueCode = async () => {
      const response = await getCode("code", "paymentMethord", "Credit Period")
      if (!props?.editItem?.modal) {
        setValue("code", response?.code?.code)
      }
      setValue("code_edit", response?.code?.manualoverride)
    }
    getUniqueCode()
  }, [])

  useEffect(() => {
    if (props?.editItem?.modal) {
      console.log(`props?.editItem?.modal`, props?.editItem?.modal)

      reset({
        ...props?.editItem?.value,
        unit: props?.editItem?.value?.unit
          ? {
              label: props?.editItem?.value?.unit === "days" ? "Days" : "Month",
              value: props?.editItem?.value?.unit,
            }
          : null,
      })
    }
  }, [props?.editItem?.modal])

  const handleUnitAdd = async data => {
    try {
      setIsLoading(true)
      const createdSeries = await postSettingsPaymentType({
        ...data,
        unit: data?.unit?.value,
        period: data?.period ? Number(data?.period) : 0,
        value_in_days:
          data?.unit?.value === "days"
            ? Number(data?.period)
            : data?.period
            ? Number(data?.period) * 30
            : 0,
      })
      toastSuccess(createdSeries?.message)
      onUpdate()
    } catch (err) {
      toastError(err?.message)
    } finally {
      setIsLoading(false)
      reset()
      close()
    }
  }

  return (
    <>
      <ModalHeader className="custom-modal-header">
        <div className="d-flex justify-content-between">
          <h5>
            {props?.editItem?.modal ? "Edit Payment Type" : "Add Payment Type"}
          </h5>{" "}
          <img
            src={closeIcon}
            alt=""
            onClick={close}
            width="10"
            role="button"
          />
        </div>
      </ModalHeader>
      {loading ? (
        <div
          className="px-8"
          style={{
            width: "100%",
            textAlign: "center",
            minHeight: "200px",
            paddingTop: "90px",
          }}
        >
          <Spinner
            className={classnames({
              "mx-2": true,
              "position-relative": true,
              visible: true,
            })}
            size="lg"
          />
        </div>
      ) : (
        <>
          <ModalBody>
            <FormProvider {...methods}>
              <Form id={`ev-form`} onSubmit={handleSubmit(handleUnitAdd)}>
                <Row>
                  <Col sm={12}>
                    <FormGroup className="mb-4">
                      <Label>Code</Label>
                      <UFInput
                        control={control}
                        placeholder=""
                        name={"code"}
                        invalid={!!errors.code}
                        disabled={
                          watch("code_edit") == 0 ||
                          watch("code_edit") == undefined
                            ? true
                            : false
                        }
                      />
                      <FormFeedback invalid>
                        {errors.code?.message}
                      </FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col sm={12}>
                    <FormGroup className="mb-4">
                      <Label>
                        Payment Type<span className="mandatory">*</span>
                      </Label>
                      <UFInput
                        control={control}
                        placeholder="Enter the Payment Type"
                        name={"name"}
                        invalid={!!errors.name}
                      />
                      <FormFeedback invalid>
                        {errors.name?.message}
                      </FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col sm={12}>
                    <FormGroup className="mb-4">
                      <Label>Period</Label>
                      <UFInputNumber
                        control={control}
                        placeholder="Enter the Period"
                        name={"period"}
                        invalid={!!errors.period}
                      />
                      <FormFeedback invalid>
                        {errors.period?.message}
                      </FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col sm={12}>
                    <FormGroup className="mb-4">
                      <Label>Type</Label>
                      <DayMonth
                        control={control}
                        placeholder="Select Type"
                        name={"unit"}
                        invalid={!!errors.unit}
                      />
                      <FormFeedback invalid>
                        {errors.unit?.message}
                      </FormFeedback>
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
            </FormProvider>
          </ModalBody>
          <ModalFooter>
            <LoadingButton
              form={`ev-form`}
              color={"primary"}
              loading={isLoading}
              disabled={isLoading}
              type="submit"
            >
              {props?.editItem?.modal ? "Update" : "Save"}
            </LoadingButton>
          </ModalFooter>
        </>
      )}
    </>
  )
}

const EditPaymentType = ({ buttonLabel, className, ...props }) => {
  const [modal, setModal] = useState(false)
  const toggle = () => setModal(!modal)

  useEffect(() => {
    if (props?.editItem?.modal) {
      toggle()
    }
  }, [props?.editItem?.modal])

  return (
    <>
      <Link to="#" className={className} onClick={toggle}>
        {buttonLabel}
      </Link>
      <Modal isOpen={modal} toggle={toggle} backdrop={"static"}>
        <Edit
          close={() => {
            setModal(false)
            props?.onReset()
          }}
          {...props}
        />
      </Modal>
    </>
  )
}

export default EditPaymentType
